import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { HelpCircle, MapPin, MessageSquare, Search, StickyNote, Trash2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ApiError,
  deleteEntry,
  getCostCodes,
  getCustomers,
  updateEntry,
  entryFromConflictDetail,
  messageFromConflictDetail,
  type Entry,
  type EntryUpdatePayload,
} from "@/lib/api";
import { displayPunchAction, displayPunchActionLabel, fmtPunchTime, toDatetimeLocalET } from "@/lib/time";
import { formatDuration } from "@/pages/dashboard/formatDuration";

export function hasPunchNotes(e: Entry): boolean {
  const nonempty = (v: string | null | undefined) => Boolean(v && v.trim());
  return (
    nonempty(e.task) ||
    nonempty(e.notes) ||
    nonempty(e.clock_out_task) ||
    nonempty(e.clock_out_notes) ||
    nonempty(e.injury_notes) ||
    nonempty(e.break_notes)
  );
}

/** True when the punch-level "Add note" / field_notes column has text. */
export function hasPunchFieldNotes(e: Entry): boolean {
  return Boolean(e.field_notes && e.field_notes.trim());
}

/** Any notes worth opening the View notes dialog (form notes + additional note). */
export function hasAnyViewableNotes(e: Entry): boolean {
  return hasPunchNotes(e) || hasPunchFieldNotes(e);
}

export function displayNoteText(v: string | null | undefined): string {
  const t = (v ?? "").trim();
  return t || "(none)";
}

export function EntryTable({
  entries,
  compact = false,
  currentUserId,
  showQbLink = true,
  canEditPunches = false,
  editableUserIds = null,
  canDeletePunches = false,
  onEditFieldNotes,
  onEntryUpdated,
  onEntryDeleted,
}: {
  entries: Entry[];
  compact?: boolean;
  currentUserId?: number;
  /** Hide for subcontractors — they have no QuickBase login. Default true for admin reports. */
  showQbLink?: boolean;
  /** Admins/managers can click-edit site, cost code, clock-in/out. */
  canEditPunches?: boolean;
  /**
   * When set (dual manager+readonly account view), only these user_ids get edit UI.
   * null = no extra restriction (admin or manager-only team-scoped list).
   */
  editableUserIds?: Set<number> | null;
  /** Full admins only — delete punch (archive + QB purge). */
  canDeletePunches?: boolean;
  /** Add/edit punch-level additional note — owner only (Employee Recent Activity). */
  onEditFieldNotes?: (entry: Entry) => void;
  onEntryUpdated?: (entry: Entry) => void;
  onEntryDeleted?: (entryId: number) => void;
}) {
  const canShowQb = Boolean(showQbLink) && !compact;
  const [localEntries, setLocalEntries] = React.useState(entries);
  React.useEffect(() => {
    setLocalEntries(entries);
  }, [entries]);
  const [deleteTarget, setDeleteTarget] = React.useState<Entry | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const canEditEntry = React.useCallback(
    (entry: Entry) => {
      if (!canEditPunches) return false;
      if (editableUserIds == null) return true;
      return editableUserIds.has(entry.user_id);
    },
    [canEditPunches, editableUserIds],
  );

  const confirmDeletePunch = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteEntry(deleteTarget.id);
      setLocalEntries((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      onEntryDeleted?.(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : "Failed to delete punch.");
    } finally {
      setDeleting(false);
    }
  };

  type EditTarget =
    | { kind: "site" | "cost_code"; entry: Entry }
    | { kind: "clock_in" | "clock_out"; entry: Entry }
    | null;
  const [editTarget, setEditTarget] = React.useState<EditTarget>(null);
  const [pickerSearch, setPickerSearch] = React.useState("");
  const [timeValue, setTimeValue] = React.useState("");
  const [editError, setEditError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  /** Read-only modal for the four punch note fields (clock-in/out, injury, meals). */
  const [notesViewEntry, setNotesViewEntry] = React.useState<Entry | null>(null);
  /** Outside-geofence detail: show recorded coords + Google Maps link. */
  const [geoDetail, setGeoDetail] = React.useState<{
    entry: Entry;
    side: "in" | "out";
  } | null>(null);

  const { data: pickerCustomers } = useQuery({
    queryKey: ["entry-edit-customers", pickerSearch],
    queryFn: () => getCustomers(pickerSearch),
    enabled: editTarget?.kind === "site",
  });
  const { data: pickerCostCodes } = useQuery({
    queryKey: ["entry-edit-cost-codes", pickerSearch],
    queryFn: () => getCostCodes(pickerSearch),
    enabled: editTarget?.kind === "cost_code",
  });

  const openSiteEdit = (entry: Entry) => {
    setEditError(null);
    setPickerSearch("");
    setEditTarget({ kind: "site", entry });
  };
  const openCostEdit = (entry: Entry) => {
    setEditError(null);
    setPickerSearch("");
    setEditTarget({ kind: "cost_code", entry });
  };
  const openTimeEdit = (entry: Entry, kind: "clock_in" | "clock_out") => {
    setEditError(null);
    const raw = kind === "clock_in" ? entry.recorded_time : entry.clock_out_time;
    setTimeValue(toDatetimeLocalET(raw));
    setEditTarget({ kind, entry });
  };

  const applyUpdatedEntry = (updated: Entry) => {
    setLocalEntries((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
    onEntryUpdated?.(updated);
  };

  const saveEdit = async (payload: EntryUpdatePayload) => {
    if (!editTarget) return;
    setSaving(true);
    setEditError(null);
    try {
      const updated = await updateEntry(editTarget.entry.id, payload);
      applyUpdatedEntry(updated);
      setEditTarget(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const pulled = entryFromConflictDetail(err.detail);
        if (pulled) applyUpdatedEntry(pulled);
        setEditError(
          messageFromConflictDetail(err.detail) ||
            "QuickBase was newer; latest values were pulled down."
        );
      } else if (err instanceof ApiError) {
        setEditError(err.message);
      } else {
        setEditError("Failed to save changes.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (localEntries.length === 0) {
    return <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">No records yet.</div>;
  }

  const editableCellClass =
    "cursor-pointer rounded px-1 -mx-1 hover:bg-muted/70 hover:underline decoration-dotted underline-offset-2";
  const correctedCellClass = "font-bold text-amber-700 dark:text-amber-400";
  const cellClass = (editable: boolean, corrected?: boolean) =>
    [editable ? editableCellClass : undefined, corrected ? correctedCellClass : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  const cellTitle = (...parts: Array<string | undefined>) =>
    parts.filter(Boolean).join(" ") || undefined;

  const headHelpClass = "inline-flex cursor-help text-muted-foreground/80 hover:text-foreground";

  const HeadLabel = ({
    label,
    help,
    helpClassName = headHelpClass,
  }: {
    label: string;
    help: string;
    helpClassName?: string;
  }) => (
    <span className="inline-flex items-center gap-1">
      {label}
      <span className={helpClassName} title={help}>
        <HelpCircle className="size-3.5 shrink-0" aria-hidden />
        <span className="sr-only">{help}</span>
      </span>
    </span>
  );

  return (
    <div className="space-y-3">
      {!compact && (
        <div
          className="flex flex-wrap items-start gap-x-5 gap-y-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
          role="note"
          aria-label="Report color key"
        >
          <span className="font-medium text-foreground">Key:</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-3 shrink-0 rounded-sm bg-rose-50 ring-1 ring-rose-200 dark:bg-rose-950/50 dark:ring-rose-800" />
            Pink row: outside geofence (clock-in or out)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-3 shrink-0 rounded-sm bg-sky-100 ring-1 ring-sky-300 dark:bg-sky-950/40 dark:ring-sky-700" />
            Blue notes cell: punch has notes (view / additional)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <StickyNote className="size-3.5 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
            Blue note icon next to Action when notes exist
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="font-bold text-red-600 dark:text-red-400">Yes</span>
            = outside fence (click for GPS / Maps)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="font-bold text-amber-700 dark:text-amber-400">Time</span>
            = recorded time, site, or cost code was corrected
          </span>
        </div>
      )}
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <HeadLabel
                label="Action"
                help="Clock in or clock out for this punch row (open shifts show as clock in until closed)."
              />
            </TableHead>
            <TableHead>
              <HeadLabel
                label="Employee"
                help="Employee name for this punch."
              />
            </TableHead>
            {canShowQb && (
              <TableHead>
                <HeadLabel
                  label="QB"
                  help="Open the matching QuickBase timesheet record when this punch has synced."
                />
              </TableHead>
            )}
            {/* {!compact && <TableHead>Company</TableHead>} */}
            <TableHead>
              <HeadLabel
                label="Site"
                help="Customer / job site selected for this punch. Admins and managers can click to edit when permitted."
              />
            </TableHead>
            <TableHead>
              <HeadLabel
                label="Cost Code"
                help="Labor cost code for this punch. Admins and managers can click to edit when permitted."
              />
            </TableHead>
            <TableHead>
              <HeadLabel
                label="Clock In"
                help="Clock-in time (company Eastern display plus original capture timezone when available)."
              />
            </TableHead>
            {!compact && (
              <TableHead>
                <HeadLabel
                  label="Clock Out"
                  help="Clock-out time for this shift, or Open if still clocked in."
                />
              </TableHead>
            )}
            {!compact && (
              <TableHead>
                <HeadLabel
                  label="Duration"
                  help="Hours worked between clock-in and clock-out on this row."
                />
              </TableHead>
            )}
            {/* {!compact && <TableHead>GPS</TableHead>} */}
            {!compact && (
              <TableHead>
                <HeadLabel
                  label="Out. Geo In"
                  help="Outside GeoFence when clocked in — employee punched in outside the site geofence radius. Click Yes for GPS / Maps. The whole row is pink when outside on in or out."
                />
              </TableHead>
            )}
            {!compact && (
              <TableHead>
                <HeadLabel
                  label="Dist In (mi)"
                  help="Distance in miles from the site geofence when clocking in (how far outside the fence). Click when outside for GPS / Maps."
                />
              </TableHead>
            )}
            {!compact && (
              <TableHead>
                <HeadLabel
                  label="Out. Geo Out"
                  help="Outside GeoFence when clocked out — employee punched out outside the site geofence radius. Click Yes for GPS / Maps. The whole row is pink when outside on in or out."
                />
              </TableHead>
            )}
            {!compact && (
              <TableHead>
                <HeadLabel
                  label="Dist Out (mi)"
                  help="Distance in miles from the site geofence when clocking out (how far outside the fence). Click when outside for GPS / Maps."
                />
              </TableHead>
            )}
            <TableHead>
              <HeadLabel
                label="Notes"
                help="View clock-in/out task and notes, injury, meals notes, and the additional note (synced into QuickBase audit notes). Cell is blue only when any of those exist. Standalone Field Notes are under Admin → Field Notes."
              />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {localEntries.map((entry) => {
            const displayAction = displayPunchAction(entry);
            const punchFieldNotes = hasPunchFieldNotes(entry);
            const showNotesBtn = hasAnyViewableNotes(entry);
            // Additional notes: only the punch owner may add/edit (Employee tab).
            // Admin/manager report: View notes only.
            const canEditThisFieldNote =
              Boolean(onEditFieldNotes) && entry.user_id === currentUserId;
            // Dual manager+readonly: account view but only team rows are editable.
            const rowEditable = canEditEntry(entry);
            const outsideIn = entry.clock_in_outside_geofence === true;
            const outsideOut = entry.clock_out_outside_geofence === true;
            const outsideAny = outsideIn || outsideOut;
            // Standalone Field Notes live on their own table / admin section — not row-linked
            // to punches, so we only tint rows for geofence (no purple "both" state).
            const rowHighlight = outsideAny
              ? "bg-rose-50/80 dark:bg-rose-950/35"
              : undefined;
            return (
            <TableRow key={entry.id} className={rowHighlight}>
              <TableCell className="whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5">
                  <Badge variant={displayAction === "clock_in" ? "default" : "secondary"}>
                    {displayPunchActionLabel(displayAction)}
                  </Badge>
                  {showNotesBtn && (
                    <span title="Has notes" className="inline-flex">
                      <StickyNote
                        className="size-3.5 shrink-0 text-sky-600 dark:text-sky-400"
                        aria-label="Has notes"
                      />
                    </span>
                  )}
                </span>
              </TableCell>
              <TableCell className="font-medium whitespace-nowrap">{entry.employee_name || entry.technician_name}</TableCell>
              {canShowQb && (
                <TableCell className="whitespace-nowrap">
                  {entry.qb_record_url ? (
                    <a
                      href={entry.qb_record_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                    >
                      Open
                    </a>
                  ) : (
                    <span className="text-muted-foreground text-sm">{entry.qb_record_id ? "—" : "Pending"}</span>
                  )}
                </TableCell>
              )}
              {/* Company cell hidden — header is commented out; leaving the cell
                  shifts every following column (Dist In showed GPS coords). */}
              {/* {!compact && <TableCell className="whitespace-nowrap">{entry.company_name}</TableCell>} */}
              <TableCell className="whitespace-nowrap">
                <span
                  className={cellClass(rowEditable, entry.corrected_site)}
                  title={cellTitle(
                    entry.corrected_site ? "This site was corrected." : undefined,
                    rowEditable ? "Click to change site" : undefined,
                  )}
                  onClick={rowEditable ? () => openSiteEdit(entry) : undefined}
                  role={rowEditable ? "button" : undefined}
                >
                  {entry.site_name}
                </span>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <span
                  className={cellClass(rowEditable, entry.corrected_cost_code)}
                  title={cellTitle(
                    entry.corrected_cost_code ? "This cost code was corrected." : undefined,
                    rowEditable ? "Click to change cost code" : undefined,
                  )}
                  onClick={rowEditable ? () => openCostEdit(entry) : undefined}
                  role={rowEditable ? "button" : undefined}
                >
                  {entry.cost_code_name ?? "Unassigned"}
                </span>
              </TableCell>
              <TableCell
                className="whitespace-nowrap"
                title={cellTitle(
                  entry.corrected_clock_in ? "This recorded time was corrected." : undefined,
                  fmtPunchTime(entry.recorded_time, entry.tz_name).title,
                )}
              >
                <div
                  className={cellClass(rowEditable, entry.corrected_clock_in)}
                  onClick={rowEditable ? () => openTimeEdit(entry, "clock_in") : undefined}
                  role={rowEditable ? "button" : undefined}
                  title={cellTitle(
                    entry.corrected_clock_in ? "This recorded time was corrected." : undefined,
                    fmtPunchTime(entry.recorded_time, entry.tz_name).title,
                    rowEditable ? "Click to set clock-in override" : undefined,
                  )}
                >
                  {(() => {
                    const p = fmtPunchTime(entry.recorded_time, entry.tz_name);
                    return (
                      <>
                        <div>{p.primary}</div>
                        {p.secondary && <div className="text-[10px] text-muted-foreground">{p.secondary}</div>}
                      </>
                    );
                  })()}
                </div>
              </TableCell>
              {!compact && (
                <TableCell className="whitespace-nowrap" title={cellTitle(
                  entry.corrected_clock_out ? "This recorded time was corrected." : undefined,
                  fmtPunchTime(entry.clock_out_time, entry.tz_name, entry.clock_out_tz_name).title,
                )}>
                  {entry.clock_out_time ? (
                    <div
                      className={cellClass(rowEditable, entry.corrected_clock_out)}
                      onClick={rowEditable ? () => openTimeEdit(entry, "clock_out") : undefined}
                      role={rowEditable ? "button" : undefined}
                      title={cellTitle(
                        entry.corrected_clock_out ? "This recorded time was corrected." : undefined,
                        fmtPunchTime(entry.clock_out_time, entry.tz_name, entry.clock_out_tz_name).title,
                        rowEditable ? "Click to set clock-out override" : undefined,
                      )}
                    >
                      {(() => {
                        const p = fmtPunchTime(entry.clock_out_time, entry.tz_name, entry.clock_out_tz_name);
                        return (
                          <>
                            <div>{p.primary}</div>
                            {p.secondary && <div className="text-[10px] text-muted-foreground">{p.secondary}</div>}
                          </>
                        );
                      })()}
                    </div>
                  ) : rowEditable ? (
                    <span
                      className={editableCellClass}
                      onClick={() => openTimeEdit(entry, "clock_out")}
                      role="button"
                      title="Click to set clock-out override"
                    >
                      {displayAction === "clock_in" ? <Badge variant="secondary">Open</Badge> : "—"}
                    </span>
                  ) : (
                    displayAction === "clock_in" ? <Badge variant="secondary">Open</Badge> : "—"
                  )}
                </TableCell>
              )}
              {!compact && (
                <TableCell className="whitespace-nowrap font-medium">
                  {entry.duration !== null && entry.duration !== undefined ? formatDuration(entry.duration) : "—"}
                </TableCell>
              )}
              {/* GPS cell hidden — header is commented out; was shifting Dist In (mi) onto coords. */}
              {/* {!compact && <TableCell className="whitespace-nowrap">{entry.gps_lat && entry.gps_lon ? `${entry.gps_lat}, ${entry.gps_lon}` : "Missing"}</TableCell>} */}
              {!compact && (
                <TableCell
                  className="whitespace-nowrap"
                  title={outsideIn ? "Click to view clock-in GPS coordinates" : "Outside GeoFence when clocked in"}
                >
                  {outsideIn ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-bold text-red-600 underline-offset-2 hover:underline dark:text-red-400"
                      onClick={() => setGeoDetail({ entry, side: "in" })}
                    >
                      <MapPin className="size-3.5 shrink-0" aria-hidden />
                      Yes
                    </button>
                  ) : entry.clock_in_outside_geofence === false ? (
                    <span className="text-muted-foreground">No</span>
                  ) : (
                    "—"
                  )}
                </TableCell>
              )}
              {!compact && (
                <TableCell
                  className={`whitespace-nowrap ${outsideIn ? "font-medium text-red-700 dark:text-red-300" : ""}`}
                  title={outsideIn ? "Click to view clock-in GPS coordinates" : "Distance outside geofence at clock-in (miles)"}
                >
                  {outsideIn && entry.clock_in_outside_geofence_distance != null ? (
                    <button
                      type="button"
                      className="underline-offset-2 hover:underline"
                      onClick={() => setGeoDetail({ entry, side: "in" })}
                    >
                      {entry.clock_in_outside_geofence_distance.toFixed(2)}
                    </button>
                  ) : entry.clock_in_outside_geofence_distance != null ? (
                    entry.clock_in_outside_geofence_distance.toFixed(2)
                  ) : (
                    "—"
                  )}
                </TableCell>
              )}
              {!compact && (
                <TableCell
                  className="whitespace-nowrap"
                  title={outsideOut ? "Click to view clock-out GPS coordinates" : "Outside GeoFence when clocked out"}
                >
                  {outsideOut ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-bold text-red-600 underline-offset-2 hover:underline dark:text-red-400"
                      onClick={() => setGeoDetail({ entry, side: "out" })}
                    >
                      <MapPin className="size-3.5 shrink-0" aria-hidden />
                      Yes
                    </button>
                  ) : entry.clock_out_outside_geofence === false ? (
                    <span className="text-muted-foreground">No</span>
                  ) : (
                    "—"
                  )}
                </TableCell>
              )}
              {!compact && (
                <TableCell
                  className={`whitespace-nowrap ${outsideOut ? "font-medium text-red-700 dark:text-red-300" : ""}`}
                  title={outsideOut ? "Click to view clock-out GPS coordinates" : "Distance outside geofence at clock-out (miles)"}
                >
                  {outsideOut && entry.clock_out_outside_geofence_distance != null ? (
                    <button
                      type="button"
                      className="underline-offset-2 hover:underline"
                      onClick={() => setGeoDetail({ entry, side: "out" })}
                    >
                      {entry.clock_out_outside_geofence_distance.toFixed(2)}
                    </button>
                  ) : entry.clock_out_outside_geofence_distance != null ? (
                    entry.clock_out_outside_geofence_distance.toFixed(2)
                  ) : (
                    "—"
                  )}
                </TableCell>
              )}
              <TableCell
                className={
                  showNotesBtn
                    ? "bg-sky-100/90 dark:bg-sky-950/50"
                    : undefined
                }
              >
                <div className="flex flex-wrap items-center gap-2">
                  {showNotesBtn && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 border-sky-300 px-2 text-xs text-sky-800 hover:bg-sky-50 dark:border-sky-700 dark:text-sky-200"
                      onClick={() => setNotesViewEntry(entry)}
                    >
                      View notes
                    </Button>
                  )}
                  {canEditThisFieldNote && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 px-2 ${punchFieldNotes ? "text-sky-700 hover:text-sky-900 dark:text-sky-300" : ""}`}
                      onClick={() => onEditFieldNotes?.(entry)}
                    >
                      <MessageSquare className="size-3.5" />
                      {punchFieldNotes ? "Edit note" : "Add note"}
                    </Button>
                  )}
                  {canDeletePunches && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      title="Delete punch (admin only)"
                      onClick={() => {
                        setDeleteError(null);
                        setDeleteTarget(entry);
                      }}
                    >
                      <Trash2 className="size-3.5" />
                      Delete
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
          })}
        </TableBody>
      </Table>

      <Dialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete punch?</DialogTitle>
            <DialogDescription>
              This permanently removes the punch from Dispatch and QuickBase when linked.
              A recoverable archive copy is kept until the employee is permanently deleted.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="grid gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Employee: </span>
                {deleteTarget.employee_name || deleteTarget.technician_name}
              </div>
              <div>
                <span className="text-muted-foreground">Site: </span>
                {deleteTarget.site_name || "—"}
              </div>
              <div>
                <span className="text-muted-foreground">Clock-in: </span>
                {fmtPunchTime(deleteTarget.recorded_time, deleteTarget.tz_name).primary}
              </div>
              <div>
                <span className="text-muted-foreground">Clock-out: </span>
                {deleteTarget.clock_out_time
                  ? fmtPunchTime(deleteTarget.clock_out_time, deleteTarget.tz_name, deleteTarget.clock_out_tz_name).primary
                  : "Open"}
              </div>
              {deleteTarget.qb_record_id && (
                <div>
                  <span className="text-muted-foreground">QB record: </span>
                  {deleteTarget.qb_record_id}
                </div>
              )}
            </div>
          )}
          {deleteError && (
            <Alert className="border-destructive/20 bg-destructive/10 text-destructive">
              <div className="text-sm">{deleteError}</div>
            </Alert>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={deleting}
              onClick={() => {
                setDeleteTarget(null);
                setDeleteError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleting}
              onClick={() => void confirmDeletePunch()}
            >
              {deleting ? "Deleting…" : "Delete punch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site / cost code searchable picker */}
      <Dialog
        open={editTarget?.kind === "site" || editTarget?.kind === "cost_code"}
        onOpenChange={(open) => { if (!open) setEditTarget(null); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editTarget?.kind === "site" ? "Change site" : "Change cost code"}
            </DialogTitle>
            <DialogDescription>
              Search and select a value. Changes sync to QuickBase when the local copy is not older than QB.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder={editTarget?.kind === "site" ? "Search sites..." : "Search cost codes..."}
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
          />
          <div className="max-h-60 overflow-y-auto rounded border">
            {editTarget?.kind === "site" &&
              (pickerCustomers ?? []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted"
                  disabled={saving}
                  onClick={() => void saveEdit({ customer_id: c.id })}
                >
                  <span className="font-medium">{c.company}</span>
                  <span className="text-xs text-muted-foreground">{c.customer_id}</span>
                </button>
              ))}
            {editTarget?.kind === "cost_code" &&
              (pickerCostCodes ?? []).map((cc) => (
                <button
                  key={cc.id}
                  type="button"
                  className="flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted"
                  disabled={saving}
                  onClick={() => void saveEdit({ cost_code_id: cc.id })}
                >
                  <span className="font-medium">{cc.cost_code}</span>
                  {cc.general_ledger ? (
                    <span className="text-xs text-muted-foreground">{cc.general_ledger}</span>
                  ) : null}
                </button>
              ))}
            {editTarget?.kind === "site" && (pickerCustomers ?? []).length === 0 && (
              <div className="p-3 text-sm text-muted-foreground">No sites found.</div>
            )}
            {editTarget?.kind === "cost_code" && (pickerCostCodes ?? []).length === 0 && (
              <div className="p-3 text-sm text-muted-foreground">No cost codes found.</div>
            )}
          </div>
          {editError && <p className="text-sm text-destructive">{editError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clock-in / clock-out override dialog */}
      <Dialog
        open={editTarget?.kind === "clock_in" || editTarget?.kind === "clock_out"}
        onOpenChange={(open) => { if (!open) setEditTarget(null); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editTarget?.kind === "clock_in" ? "Set clock-in override" : "Set clock-out override"}
            </DialogTitle>
            <DialogDescription>
              Value is Eastern (export) wall time and is written to the QuickBase override field so the effective timestamp updates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="override-time">Override timestamp (ET)</Label>
            <Input
              id="override-time"
              type="datetime-local"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
            />
          </div>
          {editError && <p className="text-sm text-destructive">{editError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>Cancel</Button>
            <Button
              disabled={saving || !timeValue}
              onClick={() => {
                if (!editTarget || (editTarget.kind !== "clock_in" && editTarget.kind !== "clock_out")) return;
                // Naive wall time; backend attaches export_tz (America/Detroit).
                const payload: EntryUpdatePayload =
                  editTarget.kind === "clock_in"
                    ? { recorded_time: `${timeValue}:00` }
                    : { clock_out_time: `${timeValue}:00` };
                void saveEdit(payload);
              }}
            >
              {saving ? "Saving..." : "Save override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outside-geofence GPS detail + Google Maps */}
      <Dialog
        open={geoDetail != null}
        onOpenChange={(open) => {
          if (!open) setGeoDetail(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {geoDetail?.side === "out" ? "Clock-out location" : "Clock-in location"}
            </DialogTitle>
            <DialogDescription>
              {geoDetail
                ? `${geoDetail.entry.employee_name || geoDetail.entry.technician_name} · outside geofence`
                : null}
            </DialogDescription>
          </DialogHeader>
          {geoDetail && (() => {
            const e = geoDetail.entry;
            const lat = geoDetail.side === "in" ? e.gps_lat : e.clock_out_gps_lat;
            const lon = geoDetail.side === "in" ? e.gps_lon : e.clock_out_gps_lon;
            const dist =
              geoDetail.side === "in"
                ? e.clock_in_outside_geofence_distance
                : e.clock_out_outside_geofence_distance;
            const hasCoords =
              lat != null &&
              lon != null &&
              String(lat).trim() !== "" &&
              String(lon).trim() !== "" &&
              String(lat).toLowerCase() !== "none" &&
              String(lon).toLowerCase() !== "none";
            const mapsUrl = hasCoords
              ? `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lon}`)}`
              : null;
            return (
              <div className="grid gap-3 text-sm">
                <div>
                  <div className="font-medium text-muted-foreground">Site</div>
                  <p className="mt-0.5">{e.site_name || "—"}</p>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">Distance outside fence</div>
                  <p className="mt-0.5">
                    {dist != null ? `${Number(dist).toFixed(2)} mi` : "—"}
                  </p>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">Recorded coordinates</div>
                  {hasCoords ? (
                    <p className="mt-0.5 font-mono text-xs sm:text-sm">
                      {lat}, {lon}
                    </p>
                  ) : (
                    <p className="mt-0.5 text-muted-foreground">No GPS coordinates on this punch.</p>
                  )}
                </div>
                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    <MapPin className="size-4 shrink-0" aria-hidden />
                    Open in Google Maps
                  </a>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setGeoDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read-only punch task/notes (clock-in/out, injury, meals) + additional note (field_notes → QB audit) */}
      <Dialog
        open={notesViewEntry != null}
        onOpenChange={(open) => {
          if (!open) setNotesViewEntry(null);
        }}
      >
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Punch notes</DialogTitle>
            <DialogDescription>
              {notesViewEntry
                ? `${notesViewEntry.employee_name || notesViewEntry.technician_name} · ${
                    fmtPunchTime(notesViewEntry.recorded_time, notesViewEntry.tz_name).primary
                  }`
                : null}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 text-sm min-w-0">
            {(
              [
                ["Clock-in Task", notesViewEntry?.task],
                ["Clock-in Notes", notesViewEntry?.notes],
                ["Clock-out Task", notesViewEntry?.clock_out_task],
                ["Clock-out Notes", notesViewEntry?.clock_out_notes],
                ["Meals & Breaks", notesViewEntry?.meals_breaks],
                ["Meals & Breaks Notes", notesViewEntry?.break_notes],
                ["Injured", notesViewEntry?.injured],
                ["Injured Notes", notesViewEntry?.injury_notes],
                ["Additional note", notesViewEntry?.field_notes],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="min-w-0">
                <div className="font-medium text-muted-foreground">{label}</div>
                <p className="mt-0.5 whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] max-w-full">
                  {displayNoteText(value)}
                </p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setNotesViewEntry(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}
